import { Injector, inject, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { SettingsService, TitleService, _HttpClient } from '@delon/theme';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Observable, of, Subject, tap } from 'rxjs';
import { FormService } from 'src/app/core/util/form.service';
import { AttachmentChangePayload } from 'src/app/shared/models/attachment-change.model';
import { DateFormatService } from 'src/app/shared/services/date-format.service';
import { UserDefaultService } from 'src/app/shared/services/user-default.service';
import { v4 as uuidv4 } from 'uuid'; // Import the uuid function

import { EInvoiceService } from '../../../core/services/e-invoice.service';
import { CalcService } from '../../../core/util/calc.service';
import { InstantDataService } from '../instant-data.service';
import { StockBundleHelperService } from '../stock-bundle-helper.service';

export abstract class DataBaseService {
  // Used for dependency injection troubleshoting purpose only
  uuid = uuidv4();

  _data: any = {};
  private _originalData: any = this._data;

  parentComponent: any;

  private _modified = false;

  public focusObserver = new Subject<void>();

  get forceEdit() {
    return false;
  }

  get modified() {
    return this._modified;
  }
  set modified(value: boolean) {
    const isQuickscan = this.reuseTab.curUrl.startsWith(
      '/ai/quickscan/convert'
    );
    this._modified = value;
    if (!isQuickscan && this.reuseTab) {
      this.reuseTab.closable = !this._modified;
    }
  }

  dataInitialized = new Subject<boolean>();
  /** Used by documents/SST/quickscan so attachment-table can flush `cachedList` after save (not used for master-data create; see {@link persistCapturedMasterAttachments}). */
  eventTrigger = new Subject<any>();
  /** Snapshot when saving a new master record (/0 → id); POST runs here so it survives route teardown. */
  private _attachmentsDraftSnapshot: any[] | null = null;
  instantData: any = { enabled: false };
  loading = false;
  deleting = false;
  title: string = '';
  code: string = '';
  isBIR2307Issued: boolean | null = false;

  aclKey: string | '' = '';
  parentMenuAclKey: string | '' = '';

  private _tabPrefix = '';

  get tabPrefix() {
    if (this._tabPrefix) return this._tabPrefix;
    return this.aclKey.toUpperCase();
  }
  set tabPrefix(value: string) {
    this._tabPrefix = value;
  }

  editing = false;

  observeChanges = false;

  // Used for Ocr Transaction to disable events from original service
  disableChanges: any = {};

  get fb() {
    return this.injector.get(FormBuilder);
  }

  get reuseTab() {
    return this.injector.get(ReuseTabService);
  }

  abstract form: FormGroup;

  private _dateFormat = '';
  get dateFormat(): string {
    const dateFormatService = this.injector.get(DateFormatService);
    return (
      dateFormatService.currentDateFormatValue ||
      this._dateFormat ||
      dateFormatService.getDefaultDateFormat()
    );
  }
  set dateFormat(value: string) {
    this._dateFormat = value;
  }

  constructor(protected injector: Injector) {
    const dateFormatService = injector.get(DateFormatService);
    this.dateFormat =
      dateFormatService.currentDateFormatValue ||
      dateFormatService.getDefaultDateFormat();
  }

  get titleSrv() {
    return this.injector.get(TitleService);
  }

  get formSrv() {
    return this.injector.get(FormService);
  }

  get http() {
    return this.injector.get(_HttpClient);
  }

  get calc() {
    return this.injector.get(CalcService);
  }

  get bundleHelper() {
    return this.injector.get(StockBundleHelperService);
  }

  get modalService() {
    return this.injector.get(NzModalService);
  }

  get eInvoiceService() {
    return this.injector.get(EInvoiceService);
  }

  get einvEffectiveDate() {
    return this.eInvoiceService.effectiveDate();
  }

  get instantDataService() {
    return this.injector.get(InstantDataService);
  }

  get isEInvStockRequired(): boolean {
    // If the E-Invoice feature is not enabled, return false
    if (!this.eInvoiceService.isEnabled) return false;

    return this.eInvoiceService.requireStockValidation();
  }

  get isEInvEnabled() {
    return this.eInvoiceService.isEnabled;
  }

  // Remove later
  get isEInvReqSystemData() {
    return false;
  }

  public readonly msg = inject(NzMessageService);
  public readonly fs = inject(FormService);
  protected readonly userDefaultService = inject(UserDefaultService);

  get country() {
    return this.injector.get(SettingsService).user['company']?.['country'];
  }

  get parkAndPost() {
    return this.injector.get(SettingsService).user['company']?.[
      'isParkAndPostEnable'
    ];
  }

  get displayARAPJournalByControlAccount() {
    return this.injector.get(SettingsService).user['company']?.[
      'displayARAPJournalByControlAccount'
    ];
  }

  nextRecordUrl: string = '';
  previousRecordUrl: string = '';

  abstract initForm(data: any): void;
  abstract new(): Observable<any>;
  abstract get(id: string): Observable<any>;
  abstract create(body: any): Observable<any>;
  abstract update(body: any): Observable<any>;
  abstract delete(id: string): Promise<any>;
  abstract clone(): any;

  nextRecord(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.post(this.nextRecordUrl, { currentCode: this.code }).subscribe({
        next: (res: any) => {
          resolve(res);
        }
      });
    });
  }

  previousRecord(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http
        .post(this.previousRecordUrl, { currentCode: this.code })
        .subscribe({
          next: (res: any) => {
            resolve(res);
          }
        });
    });
  }

  focus() {
    this.focusObserver.next();
  }

  setFromRecurring() {
    if (this.recurringId) {
      this.http
        .get(`api/recurringTransactions/${this.recurringId}`)
        .subscribe((template: any) => {
          setTimeout(() => {
            this.setData(JSON.parse(template.dataSnapshot));
          }, 500);
        });
    }
  }

  get id() {
    return this.injector.get(ActivatedRoute).snapshot.params['id'];
  }

  get recurringId(): string | null {
    return (
      this.injector.get(ActivatedRoute).snapshot.queryParams['recurringId'] ||
      null
    );
  }

  initParam(data: any): any {
    return data;
  }

  validate(): boolean {
    const { isValid, invalidControls } = this.fs.validate(this.form);
    if (!isValid) {
      this.fs.showValidationMsg(invalidControls);
      //this.displayWarningMessage(invalidControls);
      this.focusFirstInvalidControl();
    }
    return isValid;
  }

  validateAsync(): Promise<boolean> {
    return this.fs.validateAsync(this.form).then(isValid => {
      if (!isValid) {
        const invalidControls = this.fs.getInvalidControls(this.form);
        this.fs.showValidationMsg(invalidControls);
        //this.displayWarningMessage(invalidControls);
        this.focusFirstInvalidControl();
      }
      return isValid;
    });
  }

  toggleEdit(editing: boolean) {
    this.editing = editing;
    if (editing) this.injector.get(FormService).enable(this.form);
    else this.injector.get(FormService).disable(this.form);
    if (editing) this.observeChanges = true;
  }

  getValue(path: string) {
    return this.form.get(path)?.value;
  }

  resetData() {
    this.observeChanges = false;
    this.form.reset();
    this.setData({});
  }

  resetDataToOriginal() {
    this.observeChanges = false;
    this.form.reset();
    this.setData(this._originalData);
  }

  setData(data: any) {
    this.loading = true;
    this._data = data;
    this._originalData = data;
    this.observeChanges = false;
    this.initForm(data);
    this.observeChanges = false;
    data = this.initParam(data);
    this.form.patchValue(data);
    this.form.setErrors(null);
    const itemDetails = this.form.get('itemDetails') as FormArray | null;
    if (itemDetails) {
      // Normalize bundle linkage immediately after loading data.
      // Copied transactions can contain empty/placeholder ids, which prevents
      // bundle parent stock-change logic from removing old sub-items.
      this.bundleHelper.ensureBundleIds(itemDetails);
      this.bundleHelper.primeHeaderQtyMap(itemDetails);
      this.syncNumbering(itemDetails);
    }
    this.modified = false;
    this.loading = false;
    this.dataInitialized.next(true);
    this.updateCode();
  }

  notifyInstantData(data: any) {}

  removeLine(formArray: FormArray, index: number) {
    formArray.removeAt(index);
    this.syncPos(formArray);
    this.syncNumbering(formArray);
  }

  buildLines(
    formArray: FormArray,
    createLine: () => FormGroup,
    lineCount: number
  ) {
    formArray.clear();
    for (let i = 0; i < lineCount; i++) {
      let line = createLine();
      formArray.push(line);
    }
  }

  dropLine(event: any) {
    const formArray = <FormArray>event.container.data;

    // moveItemInArray(formArray, event.previousIndex, event.currentIndex);

    // formArray.forEach((item: any, index) => {
    //   item.pos = index + 1;
    // });

    this.moveFormItem(formArray, event.previousIndex, event.currentIndex);

    this.syncPos(formArray);

    this.syncNumbering(formArray);
  }

  onAction(_action: string, _data: any): Promise<boolean> {
    return new Promise(resolve => {
      resolve(true);
    });
  }

  private updateCode() {
    if (this._data.docCode) {
      this.code = this._data.docCode;
      //this.title = this._data.docCode;
    } else if (this._data.code) {
      this.code = this._data.code;
      //this.title = this._data.code;
    } else {
      this.code = '';
    }

    this.isBIR2307Issued = this._data.isBIR2307Issued;
    if (this.code.startsWith('auto-')) this.code = '';
  }

  private moveFormItem(
    formArray: FormArray,
    previousIndex: number,
    currentIndex: number
  ) {
    const direction = currentIndex > previousIndex ? 1 : -1;

    const from = previousIndex;
    const to = currentIndex;

    const temp = formArray.at(from);
    for (let i = from; i * direction < to * direction; i = i + direction) {
      const current = formArray.at(i + direction);
      formArray.setControl(i, current);
    }
    formArray.setControl(to, temp);
  }

  private syncPos(formArray: FormArray) {
    for (let i = 0; i < formArray.length; i++) {
      const formGroup = formArray.at(i);
      formGroup.patchValue({ pos: i + 1 });
    }
  }

  private syncNumbering(formArray: FormArray) {
    if (formArray.length > 0) {
      // check object has property numbering
      if (
        formArray.value[0].numbering === undefined &&
        formArray.value[0].stockId === undefined
      ) {
        return;
      }

      let numbering = 1;
      for (let i = 0; i < formArray.length; i++) {
        const item = formArray.at(i);
        if (item.value.isSubItem === true) {
          item.patchValue({ numbering: null });
          continue;
        }
        if (item.value.stockId !== null) {
          item.patchValue({ numbering: numbering });
          numbering++;
        }
      }
    }
  }

  private displayWarningMessage(invalidControls: any[]) {
    const errorMessages = invalidControls.map(control => {
      const errorList = Object.keys(control.errors).map(key => {
        switch (key) {
          case 'required':
            return `${control.key} is required.`;
          default:
            return `${control.key} has an invalid value.`;
        }
      });
      return errorList.join('<br>');
    });

    const detailedMessage = errorMessages.join('<br>'); // Join error messages with <br> for new lines in HTML
    this.msg.error(detailedMessage, { nzDuration: 6000, nzPauseOnHover: true });
  }

  private focusFirstInvalidControl(): void {
    for (const key of Object.keys(this.form.controls)) {
      const control = this.form.controls[key];
      if (control.invalid) {
        if (this.parentComponent && this.parentComponent.focusElement) {
          this.parentComponent.focusElement(key);
        } else {
          if (this.fs.focusInvalidControl()) return;
        }
        return; // Focus on the first invalid control and exit the function
      }
    }
  }

  getAttachmentChangesPayload(): AttachmentChangePayload | null {
    return null;
  }

  clearAttachmentChanges(): void {
    return;
  }

  captureAttachmentsDraft(rows?: readonly any[] | null): void {
    if (!rows?.length) {
      this._attachmentsDraftSnapshot = null;
      return;
    }
    this._attachmentsDraftSnapshot = rows.map(row => ({ ...row }));
  }

  persistCapturedMasterAttachments(referenceId: unknown): Observable<unknown> {
    const snapshot = this._attachmentsDraftSnapshot;
    const idStr =
      referenceId === undefined || referenceId === null
        ? ''
        : String(referenceId);
    if (!snapshot?.length || !idStr) {
      return of(null);
    }
    const body = snapshot.map(row => ({
      ...row,
      referenceId: idStr
    }));
    return this.http
      .post('api/attachments/create?_silent=true', body)
      .pipe(tap(() => (this._attachmentsDraftSnapshot = null)));
  }
}
